import React, { useState } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Alert, Button, Form, Row, Col, Spinner } from 'react-bootstrap';

import useAuth from '../../hooks/useAuth';
import Loader from '../Loader';
import GoogleIcon from '../../assets/img/new/googleIcon.svg';
import { Eye, EyeOff } from 'react-feather';
import { compareUserInput, generateRandomString } from '../../constants';
import TwoFactorAuth from './TwoFactorAuth';
import { useRef } from 'react';
import { MaskedInput, createDefaultMaskGenerator } from 'react-hook-mask';

function SignUp() {
  const { signUp, signUpWithGoogle, errorMessage, sendSignupVerificationCode } = useAuth();
  const signUpForm = useRef(null);
  const [code, setCode] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [verified, setVerified] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showPassword, setShowPassword] = useState({
    createPassword: false,
    newPassword: false,
  });
  const handleShowPassword = type => {
    const showPasswordOri = { ...showPassword };
    if (type === 'createPassword') {
      showPasswordOri.createPassword = !showPasswordOri.createPassword;
    }
    if (type === 'newPassword') {
      showPasswordOri.newPassword = !showPasswordOri.newPassword;
    }
    setShowPassword(showPasswordOri);
  };

  const getValidationSchema = () => {
    return Yup.object().shape({
      firstName: Yup.string().max(255).required('First name is required'),
      lastName: Yup.string().max(255).required('Last name is required'),
      phone: Yup.string().max(255).required('Phone is required'),
      email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
      password: Yup.string()
        .min(6, 'Must be at least 8 characters')
        .max(255)
        .required('Password is Required')
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/, { message: 'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character' }),
      confirmPassword: Yup.string()
        .min(6, 'Must be at least 6 characters')
        .max(255)
        .required('Confirm password required')
        .oneOf([Yup.ref('password'), null], 'Passwords must match'),
      terms_of_service: Yup.number().required('Terms of Service is required'),
    });
  };

  const phoneMask = createDefaultMaskGenerator('(999) 999 9999');

  if (showLoader) {
    <Loader />;
  }
  return (
    <>
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          phone: '',
          terms_of_service: '',
          submit: false,
        }}
        ref={signUpForm}
        validationSchema={getValidationSchema()}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          // if (!verified) {
          //   const passcode = generateRandomString();
          //   setCode(passcode);
          //   const obj = { ...values, code: passcode };
          //   !showCode && setShowCode(true);
          //   return sendSignupVerificationCode(obj);
          // }
          try {
            setShowLoader(true);
            await signUp(values.email, values.password, values.firstName, values.lastName, values.phone, setStatus, setErrors, setSubmitting);
            setShowLoader(false);
            setVerified(false);
          } catch (error) {
            setVerified(false);
            setShowLoader(false);
            const message = error.message || 'Something went wrong';
            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}>
        {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, isSubmitting, touched, values }) => (
          <>
            <Form onSubmit={handleSubmit}>
              {errors.submit && (
                <Alert className='my-3' variant='danger'>
                  {errors.submit}
                </Alert>
              )}
              {errorMessage && (
                <Alert className='my-3' variant='danger'>
                  {errorMessage}
                </Alert>
              )}
              <Row>
                <Form.Group as={Col} sm={6} className='mb-1 mb-xl-3'>
                  <Form.Label className='text-light-gray form-label'>First name</Form.Label>
                  <Form.Control size='lg' type='text' name='firstName' placeholder='First name' value={values.firstName} isInvalid={Boolean(touched.firstName && errors.firstName)} onBlur={handleBlur} onChange={handleChange} />
                  {!!touched.firstName && <Form.Control.Feedback type='invalid'>{errors.firstName}</Form.Control.Feedback>}
                </Form.Group>
                <Form.Group as={Col} sm={6} className='mb-1 mb-xl-3'>
                  <Form.Label className='text-light-gray form-label'>Last name</Form.Label>
                  <Form.Control size='lg' type='text' name='lastName' placeholder='Last name' value={values.lastName} isInvalid={Boolean(touched.lastName && errors.lastName)} onBlur={handleBlur} onChange={handleChange} />
                  {!!touched.lastName && <Form.Control.Feedback type='invalid'>{errors.lastName}</Form.Control.Feedback>}
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col} sm={6} className='mb-1 mb-xl-3'>
                  <Form.Label className='text-light-gray form-label'>Email address</Form.Label>
                  <Form.Control size='lg' type='email' name='email' placeholder='Email address' value={values.email} isInvalid={Boolean(touched.email && errors.email)} onBlur={handleBlur} onChange={handleChange} />
                  {!!touched.email && <Form.Control.Feedback type='invalid'>{errors.email}</Form.Control.Feedback>}
                </Form.Group>
                <Form.Group as={Col} sm={6} className='mb-1 mb-xl-3'>
                  <Form.Label className='text-light-gray form-label'>Phone</Form.Label>
                  <MaskedInput
                    maskGenerator={phoneMask}
                    type='text'
                    id='phone'
                    name='phone'
                    className={`form-control form-control-lg ${Boolean(touched.phone && errors.phone) ? 'is-invalid' : ''}`}
                    placeholder='(123) 456 7890'
                    size='lg'
                    onChange={e => {
                      setFieldValue('phone', e);
                    }}
                    value={values.phone}
                    isInvalid={Boolean(touched.phone && errors.phone)}
                    onBlur={handleBlur}
                  />

                  {!!touched.phone && <Form.Control.Feedback type='invalid'>{errors.phone}</Form.Control.Feedback>}
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col} sm={6} className='mb-1 mb-xl-3'>
                  <Form.Label className='text-light-gray form-label'>Create Password</Form.Label>
                  <div className={`position-relative ${Boolean(touched.password && errors.password) && 'is-invalid'}`}>
                    <Form.Control size='lg' type={showPassword.createPassword ? 'text' : 'password'} name='password' placeholder='Password' value={values.password} isInvalid={Boolean(touched.password && errors.password)} onBlur={handleBlur} onChange={handleChange} />
                    <span className='position-absolute top-50 end-0 translate-middle-y text-dark-blue' style={{ cursor: 'pointer', marginRight: '10px' }} onClick={() => handleShowPassword('createPassword')}>
                      {showPassword.createPassword ? <EyeOff /> : <Eye />}
                    </span>
                  </div>
                  {!!touched.password && <Form.Control.Feedback type='invalid'>{errors.password}</Form.Control.Feedback>}
                </Form.Group>
                <Form.Group as={Col} sm={6} className='mb-1 mb-xl-3'>
                  <Form.Label className='text-light-gray form-label'>Verify Password</Form.Label>
                  <div className={`position-relative ${Boolean(touched.confirmPassword && errors.confirmPassword) && 'is-invalid'}`}>
                    <Form.Control size='lg' type={showPassword.newPassword ? 'text' : 'password'} name='confirmPassword' placeholder='Confirm Password' value={values.confirmPassword} isInvalid={Boolean(touched.confirmPassword && errors.confirmPassword)} onBlur={handleBlur} onChange={handleChange} />
                    <span className='position-absolute top-50 end-0 translate-middle-y text-dark-blue' style={{ cursor: 'pointer', marginRight: '10px' }} onClick={() => handleShowPassword('newPassword')}>
                      {showPassword.newPassword ? <EyeOff /> : <Eye />}
                    </span>
                  </div>
                  {!!touched.confirmPassword && <Form.Control.Feedback type='invalid'>{errors.confirmPassword}</Form.Control.Feedback>}
                </Form.Group>
              </Row>

              <Form.Group className='my-3' controlId='formBasicCheckbox'>
                <style>
                  {`
                  .custom-checkbox .form-check-input {
      border: 1px solid #000e4e;
    }

                  `}
                </style>
                <Form.Check
                  name='terms_of_service'
                  className='text-dark-blue custom-checkbox'
                  type='checkbox'
                  isInvalid={Boolean(touched.terms_of_service && errors.terms_of_service)}
                  value={1}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  size={30}
                  label={
                    <>
                      I Accept the {''}
                      <a href='https://dispute.ly/terms.html' target='_blank' rel='noopener noreferrer'>
                        Terms and Conditions
                      </a>
                    </>
                  }
                />
              </Form.Group>

              <div className='text-left mb-1 mb-xl-3'>
                <Button type='submit' variant='primary w-100' size='lg' disabled={isSubmitting}>
                  Sign up {isSubmitting && <Spinner className='ms-1' as='span' animation='border' size='sm' role='status' aria-hidden='true' />}
                </Button>
              </div>
              <div className='login-divider ms-0 my-xl-5 my-4 w-100'>
                <span>or</span>
              </div>
              <div className='text-left mb-1 mb-xl-3'>
                <Button type='button' onClick={() => signUpWithGoogle()} variant='outline-primary w-100' size='lg'>
                  Continue with Google <img src={GoogleIcon} />
                </Button>
              </div>
            </Form>
            {showCode && (
              <TwoFactorAuth
                resendCode={() => {
                  setVerified(false);
                  handleSubmit();
                }}
                setTwoFAModal={setShowCode}
                mode='via text message'
                length={8}
                callback={userInput => {
                  if (compareUserInput(userInput, code)) {
                    setVerified(true);
                    setShowCode(false);
                    handleSubmit();
                  } else {
                    throw new Error('Invalid Code Entered, Enter valid verification code...');
                  }
                }}
              />
            )}
          </>
        )}
      </Formik>
    </>
  );
}

export default SignUp;
